import { useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';

import { Icon } from '../../../../components/Icon';
import { Badge, Button, Skeleton } from '../../../../components/ui';
import type { Patient, StageKind } from '../../../../types/admin';
import { useFunnelColumn, type FunnelFilters } from '../../hooks/useFunnelColumn';
import { FunnelPatientCard } from './FunnelPatientCard';
import styles from './funnel.module.scss';

type FunnelColumnProps = {
  readonly stageKind: StageKind;
  readonly filters: FunnelFilters;
  readonly onOpenPatient: (patientId: string) => void;
  readonly onPatientContextMenu: (patient: Patient, event: ReactMouseEvent) => void;
};

export function FunnelColumn({
  stageKind,
  filters,
  onOpenPatient,
  onPatientContextMenu,
}: FunnelColumnProps) {
  const { t } = useTranslation();
  const { total, groups, loadedCount, isLoading, isError, hasMore, isFetchingMore, loadMore } =
    useFunnelColumn(stageKind, filters);
  const { setNodeRef, isOver } = useDroppable({ id: stageKind });
  const [collapsedCycles, setCollapsedCycles] = useState<ReadonlySet<number | null>>(
    () => new Set(),
  );

  const stageLabel = t(`stages.${stageKind}`);

  function toggleCycle(cycleNumber: number | null) {
    setCollapsedCycles((current) => {
      const next = new Set(current);
      if (next.has(cycleNumber)) {
        next.delete(cycleNumber);
      } else {
        next.add(cycleNumber);
      }
      return next;
    });
  }

  return (
    <section
      ref={setNodeRef}
      className={`${styles.column} ${isOver ? styles.columnOver : ''}`}
      aria-label={stageLabel}
    >
      <header className={styles.columnHeader}>
        <span className={styles.columnTitle}>{stageLabel}</span>
        <Badge variant="neutral">{total}</Badge>
      </header>

      <div className={styles.columnBody}>
        {isLoading ? (
          <div className={styles.columnSkeleton}>
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} width="100%" height={64} />
            ))}
          </div>
        ) : isError ? (
          <p className={styles.columnEmpty}>{t('clients.loadErrorDescription')}</p>
        ) : groups.length === 0 ? (
          <p className={styles.columnEmpty}>{t('clients.funnel.emptyColumn')}</p>
        ) : (
          groups.map((group) => {
            const isCollapsed = collapsedCycles.has(group.cycleNumber);
            const cycleKey = group.cycleNumber ?? 'none';
            const cardsId = `funnel-${stageKind}-cycle-${cycleKey}`;

            return (
              <div key={cycleKey} className={styles.cycleGroup}>
                <button
                  type="button"
                  className={styles.cycleGroupHeader}
                  onClick={() => toggleCycle(group.cycleNumber)}
                  aria-expanded={!isCollapsed}
                  aria-controls={cardsId}
                >
                  <span>
                    {group.cycleNumber === null
                      ? t('clients.funnel.noCycle')
                      : t('clients.funnel.stageGroup', { number: group.cycleNumber })}
                  </span>
                  <span className={styles.cycleGroupHeaderMeta}>
                    <span className={styles.cycleGroupCount}>{group.patients.length}</span>
                    <span
                      className={`${styles.cycleGroupChevron} ${
                        isCollapsed ? styles.cycleGroupChevronCollapsed : ''
                      }`}
                      aria-hidden="true"
                    >
                      <Icon name="chevron-down" size={14} />
                    </span>
                  </span>
                </button>
                <div
                  id={cardsId}
                  className={`${styles.cycleGroupCollapse} ${
                    isCollapsed ? styles.cycleGroupCollapseCollapsed : ''
                  }`}
                  aria-hidden={isCollapsed}
                  inert={isCollapsed}
                >
                  <div className={styles.cycleGroupCollapseInner}>
                    <div className={styles.cycleGroupCards}>
                      {group.patients.map((patient) => (
                        <FunnelPatientCard
                          key={patient.id}
                          patient={patient}
                          stageKind={stageKind}
                          onOpen={() => onOpenPatient(patient.id)}
                          onContextMenu={(event) => onPatientContextMenu(patient, event)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {hasMore && (
        <Button variant="secondary" size="sm" fullWidth onClick={loadMore} disabled={isFetchingMore}>
          {isFetchingMore
            ? t('clients.funnel.loading')
            : t('clients.funnel.loadMore', { count: Math.max(0, total - loadedCount) })}
        </Button>
      )}
    </section>
  );
}
